import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sword, Car, Plus, X, Shield, Zap, Target, ChevronLeft } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const EquipmentPanel = ({ gameId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/game/${gameId}/equipment`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const equip = async (itemId, slot) => {
    setActing(itemId);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/equip`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, slot }),
      });
      if (res.ok) load();
    } catch (e) { console.error(e); }
    setActing(null);
  };

  const unequip = async (itemId, slot) => {
    setActing(itemId);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/unequip`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, slot }),
      });
      if (res.ok) load();
    } catch (e) { console.error(e); }
    setActing(null);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-5 h-5 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-center text-[#E8E0D0]/30 text-sm py-10">Failed to load equipment.</p>;

  const weapons = Object.entries(data.equipped_weapons || {});
  const vehicles = Object.entries(data.equipped_vehicles || {});
  const emptyWeaponSlots = data.max_weapon_slots - weapons.length;
  const emptyVehicleSlots = data.max_vehicle_slots - vehicles.length;

  return (
    <div className="space-y-5" data-testid="equipment-panel">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/40 text-xs tracking-widest hover:text-[#E8E0D0] transition-colors flex items-center gap-1" data-testid="equip-back-btn">
          <ChevronLeft size={14} /> BACK
        </button>
        <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">ERA {data.era}</span>
      </div>

      <div className="text-center">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#C9A84C]" data-testid="equip-title">LOADOUT</h3>
        <p className="font-['Outfit'] text-[9px] tracking-widest text-[#E8E0D0]/25 mt-1">EQUIP WEAPONS AND VEHICLES TO BOOST YOUR POWER</p>
      </div>

      {/* Bonuses Summary */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-[#0a0e12] border border-[#C9A84C]/10 rounded-lg p-3 text-center">
          <Zap size={14} className="text-[#C9A84C] mx-auto mb-1" />
          <p className="font-['Outfit'] text-[#C9A84C] text-xs font-bold">{data.bonuses.job_success}</p>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">JOB SUCCESS</p>
        </div>
        <div className="bg-[#0a0e12] border border-[#8B1A1A]/10 rounded-lg p-3 text-center">
          <Sword size={14} className="text-[#8B1A1A] mx-auto mb-1" />
          <p className="font-['Outfit'] text-[#8B1A1A] text-xs font-bold">{data.bonuses.boss_attack}</p>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">BOSS ATTACK</p>
        </div>
        <div className="bg-[#0a0e12] border border-[#3B82F6]/10 rounded-lg p-3 text-center">
          <Shield size={14} className="text-[#3B82F6] mx-auto mb-1" />
          <p className="font-['Outfit'] text-[#3B82F6] text-xs font-bold">{data.bonuses.boss_defense}</p>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">BOSS DEFENSE</p>
        </div>
      </div>

      {/* Weapon Slots */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Sword size={14} className="text-[#C9A84C]" />
          <span className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold tracking-wider">WEAPONS</span>
          <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">{weapons.length}/{data.max_weapon_slots}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {weapons.map(([id, w]) => (
            <div key={id} className="bg-[#111820] border border-[#C9A84C]/20 rounded-lg p-2.5 relative group" data-testid={`equipped-weapon-${id}`}>
              <p className="font-['Outfit'] text-[#E8E0D0] text-[10px] font-semibold truncate">{w.name}</p>
              <p className="font-['Outfit'] text-[#C9A84C] text-[8px]">+3% success, +10 ATK</p>
              <button
                onClick={() => unequip(id, 'weapons')}
                disabled={acting === id}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#8B1A1A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`unequip-${id}`}
              >
                <X size={10} className="text-white" />
              </button>
            </div>
          ))}
          {[...Array(emptyWeaponSlots)].map((_, i) => (
            <div key={`empty-w-${i}`} className="border border-dashed border-[#1A2535] rounded-lg p-2.5 flex items-center justify-center">
              <Plus size={14} className="text-[#E8E0D0]/10" />
            </div>
          ))}
        </div>
      </div>

      {/* Vehicle Slots */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Car size={14} className="text-[#3B82F6]" />
          <span className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold tracking-wider">VEHICLES</span>
          <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">{vehicles.length}/{data.max_vehicle_slots}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {vehicles.map(([id, v]) => (
            <div key={id} className="bg-[#111820] border border-[#3B82F6]/20 rounded-lg p-2.5 relative group" data-testid={`equipped-vehicle-${id}`}>
              <p className="font-['Outfit'] text-[#E8E0D0] text-[10px] font-semibold truncate">{v.name}</p>
              <p className="font-['Outfit'] text-[#3B82F6] text-[8px]">+2% success, +8 DEF</p>
              <button
                onClick={() => unequip(id, 'vehicles')}
                disabled={acting === id}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#8B1A1A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`unequip-${id}`}
              >
                <X size={10} className="text-white" />
              </button>
            </div>
          ))}
          {[...Array(emptyVehicleSlots)].map((_, i) => (
            <div key={`empty-v-${i}`} className="border border-dashed border-[#1A2535] rounded-lg p-2.5 flex items-center justify-center">
              <Plus size={14} className="text-[#E8E0D0]/10" />
            </div>
          ))}
        </div>
      </div>

      {/* Available Items to Equip */}
      {(data.available_weapons.length > 0 || data.available_vehicles.length > 0) && (
        <div>
          <p className="font-['Outfit'] text-[9px] tracking-widest text-[#E8E0D0]/20 mb-2">INVENTORY — TAP TO EQUIP</p>

          {data.available_weapons.length > 0 && (
            <div className="space-y-1.5 mb-3">
              <p className="font-['Outfit'] text-[8px] text-[#C9A84C]/40">WEAPONS ({data.available_weapons.length})</p>
              {data.available_weapons.map((item, i) => (
                <motion.button
                  key={`aw-${i}`}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => equip(item.item_id, 'weapons')}
                  disabled={acting || emptyWeaponSlots <= 0}
                  className={`w-full flex items-center justify-between p-2 bg-[#0a0e12] border rounded-lg transition-all ${
                    emptyWeaponSlots > 0 ? 'border-[#C9A84C]/10 hover:border-[#C9A84C]/40' : 'border-[#1A2535] opacity-40'
                  }`}
                  data-testid={`avail-weapon-${item.item_id}`}
                >
                  <div className="flex items-center gap-2">
                    <Sword size={12} className="text-[#C9A84C]/50" />
                    <span className="font-['Outfit'] text-[#E8E0D0] text-[10px]">{item.name}</span>
                  </div>
                  {emptyWeaponSlots > 0 ? (
                    <span className="font-['Outfit'] text-[#C9A84C] text-[8px] tracking-wider">EQUIP</span>
                  ) : (
                    <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">SLOTS FULL</span>
                  )}
                </motion.button>
              ))}
            </div>
          )}

          {data.available_vehicles.length > 0 && (
            <div className="space-y-1.5">
              <p className="font-['Outfit'] text-[8px] text-[#3B82F6]/40">VEHICLES ({data.available_vehicles.length})</p>
              {data.available_vehicles.map((item, i) => (
                <motion.button
                  key={`av-${i}`}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => equip(item.item_id, 'vehicles')}
                  disabled={acting || emptyVehicleSlots <= 0}
                  className={`w-full flex items-center justify-between p-2 bg-[#0a0e12] border rounded-lg transition-all ${
                    emptyVehicleSlots > 0 ? 'border-[#3B82F6]/10 hover:border-[#3B82F6]/40' : 'border-[#1A2535] opacity-40'
                  }`}
                  data-testid={`avail-vehicle-${item.item_id}`}
                >
                  <div className="flex items-center gap-2">
                    <Car size={12} className="text-[#3B82F6]/50" />
                    <span className="font-['Outfit'] text-[#E8E0D0] text-[10px]">{item.name}</span>
                  </div>
                  {emptyVehicleSlots > 0 ? (
                    <span className="font-['Outfit'] text-[#3B82F6] text-[8px] tracking-wider">EQUIP</span>
                  ) : (
                    <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">SLOTS FULL</span>
                  )}
                </motion.button>
              ))}
            </div>
          )}
        </div>
      )}

      {data.available_weapons.length === 0 && data.available_vehicles.length === 0 && weapons.length === 0 && vehicles.length === 0 && (
        <div className="text-center py-6">
          <Target size={24} className="text-[#E8E0D0]/10 mx-auto mb-3" />
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-sm">No equipment in inventory</p>
          <p className="font-['Outfit'] text-[#E8E0D0]/15 text-xs mt-1">Buy weapons and vehicles from Matt's Store</p>
        </div>
      )}
    </div>
  );
};

export default EquipmentPanel;
